import { Navigate, Route, Routes } from 'react-router-dom'
import {
  BuilderRoute,
  ConnectorsRoute,
  DashboardBuilderRoute,
  DashboardsRoute,
  DashboardViewerRoute,
  QueryEditorRoute,
  SavedQueriesRoute,
} from '@/builder/route'
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'
import { MODULE_NAME } from '@/constants/modules'
import { ACTION, RESOURCE } from '@/constants/permissions'
import { ROUTES } from '@/constants/routes'
import { AppLayout } from '@/layouts/AppLayout'
import { AuthLayout } from '@/layouts/AuthLayout'
import { AlarmsPage } from '@/pages/alarms/AlarmsPage'
import { AssetsPage } from '@/pages/assets/AssetsPage'
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage'
import { LoginPage } from '@/pages/auth/LoginPage'
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage'
import { GatewayDetailPage } from '@/pages/gateways/GatewayDetailPage'
import { GatewaysPage } from '@/pages/gateways/GatewaysPage'
import { OverviewPage } from '@/pages/overview/OverviewPage'
import { CustomerDetailPage } from '@/pages/partner/CustomerDetailPage'
import { CustomersPage } from '@/pages/partner/CustomersPage'
import { PartnerOverviewPage } from '@/pages/partner/PartnerOverviewPage'
import { ReportBuilderPage } from '@/pages/reports/ReportBuilderPage'
import { ReportsPage } from '@/pages/reports/ReportsPage'
import { SettingsPage } from '@/pages/settings/SettingsPage'
import { NotFoundPage } from '@/pages/system/NotFoundPage'
import { TagHistoryPage } from '@/pages/tags/TagHistoryPage'
import { TagsPage } from '@/pages/tags/TagsPage'
import { ModuleRoute, PermissionRoute, RoleRoute } from './guards'

/**
 * Route tree. Unauthenticated routes render in AuthLayout; authenticated routes
 * render inside AppLayout (shell) behind ProtectedRoute, each module gated by
 * ModuleRoute. Module screens currently render placeholders — real pages replace
 * them stage by stage (assets=4, gateways=5, tags=6, alarms=8, dashboards=9/10,
 * reports=11, partner=12, settings=13).
 */
export function AppRouter() {
  return (
    <Routes>
      {/* Unauthenticated */}
      <Route element={<AuthLayout />}>
        <Route path={ROUTES.LOGIN} element={<LoginPage />} />
        <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
        <Route path={ROUTES.RESET_PASSWORD} element={<ResetPasswordPage />} />
      </Route>

      {/* Authenticated shell */}
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route path={ROUTES.OVERVIEW} element={<OverviewPage />} />
        <Route
          path={ROUTES.ASSETS}
          element={
            <ModuleRoute module={MODULE_NAME.ASSETS}>
              <AssetsPage />
            </ModuleRoute>
          }
        />
        <Route
          path={ROUTES.ASSET_DETAIL}
          element={
            <ModuleRoute module={MODULE_NAME.ASSETS}>
              <AssetsPage />
            </ModuleRoute>
          }
        />
        <Route
          path={ROUTES.GATEWAYS}
          element={
            <ModuleRoute module={MODULE_NAME.GATEWAYS}>
              <GatewaysPage />
            </ModuleRoute>
          }
        />
        <Route
          path={ROUTES.GATEWAY_DETAIL}
          element={
            <ModuleRoute module={MODULE_NAME.GATEWAYS}>
              <GatewayDetailPage />
            </ModuleRoute>
          }
        />
        <Route
          path={ROUTES.TAGS}
          element={
            <ModuleRoute module={MODULE_NAME.TAGS}>
              <TagsPage />
            </ModuleRoute>
          }
        />
        <Route
          path={ROUTES.TAG_HISTORY}
          element={
            <ModuleRoute module={MODULE_NAME.TAGS}>
              <TagHistoryPage />
            </ModuleRoute>
          }
        />
        <Route
          path={ROUTES.ALARMS}
          element={
            <ModuleRoute module={MODULE_NAME.ALARMS}>
              <AlarmsPage />
            </ModuleRoute>
          }
        />
        <Route
          path={ROUTES.DASHBOARDS}
          element={
            <ModuleRoute module={MODULE_NAME.DASHBOARDS}>
              <DashboardsRoute />
            </ModuleRoute>
          }
        />
        <Route
          path={ROUTES.DASHBOARD_NEW}
          element={
            <ModuleRoute module={MODULE_NAME.DASHBOARDS}>
              <DashboardBuilderRoute />
            </ModuleRoute>
          }
        />
        <Route
          path={ROUTES.DASHBOARD_VIEW}
          element={
            <ModuleRoute module={MODULE_NAME.DASHBOARDS}>
              <DashboardViewerRoute />
            </ModuleRoute>
          }
        />
        <Route
          path={ROUTES.DASHBOARD_BUILD}
          element={
            <ModuleRoute module={MODULE_NAME.DASHBOARDS}>
              <DashboardBuilderRoute />
            </ModuleRoute>
          }
        />
        {/* Dashboard/Screen Builder (schema v2) — lazy, code-split module. */}
        <Route
          path={ROUTES.DASHBOARD_BUILDER}
          element={
            <ModuleRoute module={MODULE_NAME.DASHBOARDS}>
              <BuilderRoute />
            </ModuleRoute>
          }
        />
        {/* Admin authoring surfaces — authoring permission required. */}
        <Route
          path={ROUTES.CONNECTORS}
          element={
            <ModuleRoute module={MODULE_NAME.CONNECTORS}>
              <PermissionRoute resource={RESOURCE.CONNECTOR} action={ACTION.UPDATE}>
                <ConnectorsRoute />
              </PermissionRoute>
            </ModuleRoute>
          }
        />
        <Route
          path={ROUTES.SAVED_QUERIES}
          element={
            <ModuleRoute module={MODULE_NAME.DASHBOARDS}>
              <PermissionRoute resource={RESOURCE.SAVED_QUERY} action={ACTION.UPDATE}>
                <SavedQueriesRoute />
              </PermissionRoute>
            </ModuleRoute>
          }
        />
        <Route
          path={ROUTES.SAVED_QUERY_NEW}
          element={
            <ModuleRoute module={MODULE_NAME.DASHBOARDS}>
              <PermissionRoute resource={RESOURCE.SAVED_QUERY} action={ACTION.CREATE}>
                <QueryEditorRoute />
              </PermissionRoute>
            </ModuleRoute>
          }
        />
        <Route
          path={ROUTES.SAVED_QUERY_EDIT}
          element={
            <ModuleRoute module={MODULE_NAME.DASHBOARDS}>
              <PermissionRoute resource={RESOURCE.SAVED_QUERY} action={ACTION.UPDATE}>
                <QueryEditorRoute />
              </PermissionRoute>
            </ModuleRoute>
          }
        />
        <Route
          path={ROUTES.REPORTS}
          element={
            <ModuleRoute module={MODULE_NAME.REPORTS}>
              <ReportsPage />
            </ModuleRoute>
          }
        />
        <Route
          path={ROUTES.REPORT_NEW}
          element={
            <ModuleRoute module={MODULE_NAME.REPORTS}>
              <ReportBuilderPage />
            </ModuleRoute>
          }
        />
        <Route
          path={ROUTES.REPORT_EDIT}
          element={
            <ModuleRoute module={MODULE_NAME.REPORTS}>
              <ReportBuilderPage />
            </ModuleRoute>
          }
        />
        <Route
          path={ROUTES.PARTNER}
          element={
            <ModuleRoute module={MODULE_NAME.PARTNERS}>
              <RoleRoute roles={['partner-admin', 'admin']}>
                <PartnerOverviewPage />
              </RoleRoute>
            </ModuleRoute>
          }
        />
        <Route
          path={ROUTES.CUSTOMERS}
          element={
            <ModuleRoute module={MODULE_NAME.PARTNERS}>
              <RoleRoute roles={['partner-admin', 'admin']}>
                <CustomersPage />
              </RoleRoute>
            </ModuleRoute>
          }
        />
        <Route
          path={ROUTES.CUSTOMER_DETAIL}
          element={
            <ModuleRoute module={MODULE_NAME.PARTNERS}>
              <RoleRoute roles={['partner-admin', 'admin']}>
                <CustomerDetailPage />
              </RoleRoute>
            </ModuleRoute>
          }
        />
        <Route
          path={ROUTES.SETTINGS}
          element={
            <ModuleRoute module={MODULE_NAME.SETTINGS}>
              <SettingsPage />
            </ModuleRoute>
          }
        />
        <Route path="*" element={<NotFoundPage />} />
      </Route>

      {/* Root + unmatched (unauthenticated) */}
      <Route path="/" element={<Navigate to={ROUTES.OVERVIEW} replace />} />
    </Routes>
  )
}
